import { apiSlice } from "../../../features/api/apiSlice";

/**
 * Firm (practice) registration — the server-side draft flow, mirroring
 * upgradeApi. The draft lives on the server (unlike the old localStorage
 * firm wizard), so progress survives a reload.
 */
export const firmApi = apiSlice.injectEndpoints({
  endpoints: (builder) => ({
    /* ── The member's own FIRM (as its owner) ─────────────────────────────
       Separate from their firm APPLICATIONS: an application is paperwork in
       review, this is a firm that exists on the register. The owner is either
       the account attached to the firm or its principal partner. */
    getMyFirms: builder.query<any[], void>({
      query: () => "/firm/my-firms",
      transformResponse: (res: any) => res?.data ?? [],
      providesTags: ["MyFirms"],
    }),
    getMyFirm: builder.query<any, string | number>({
      query: (id) => `/firm/my-firms/${id}`,
      transformResponse: (res: any) => res?.data ?? res,
      providesTags: (_r, _e, id) => [{ type: "MyFirms" as const, id: String(id) }],
    }),
    /** OnePay payload clearing everything the firm owes, in one payment. */
    payMyFirmDues: builder.mutation<any, { id: string | number }>({
      query: ({ id }) => ({ url: `/firm/my-firms/${id}/pay`, method: "POST", body: {} }),
      invalidatesTags: ["MyFirms"],
    }),

    /* Eligibility + the member's own applications */
    getFirmEligibility: builder.query<any, void>({
      query: () => "/firm/eligibility",
      providesTags: ["FirmApplications"],
    }),
    getMyFirmApplications: builder.query<any[], void>({
      query: () => "/firm/applications",
      transformResponse: (res: any) => (Array.isArray(res) ? res : res?.data ?? []),
      providesTags: ["FirmApplications"],
    }),
    getFirmApplicationDetails: builder.query<any, string | number>({
      query: (id) => `/firm/applications/${id}`,
      providesTags: (_r, _e, id) => [{ type: "FirmApplicationDetails" as const, id: String(id) }],
    }),

    /* The in-progress draft (resumes the wizard) */
    getFirmCurrent: builder.query<any, void>({
      query: () => "/firm/applications/current",
      providesTags: ["FirmApplications"],
    }),

    /* Steps. step1 creates the draft and carries the firm's own particulars,
       its registration documents and its whole roster of people; 2, 3 and 4 were
       folded into it and 8 was dropped, so the numbering here has gaps on
       purpose — the server's endpoint numbers never moved. */
    firmStep1: builder.mutation<any, any>({
      query: (body) => ({ url: "/firm/applications/step1", method: "POST", body }),
      invalidatesTags: ["FirmApplications"],
    }),
    firmStep5: builder.mutation<any, { id: string | number; benevolence_records: any[] }>({
      query: ({ id, benevolence_records }) => ({
        url: `/firm/applications/${id}/step5`,
        method: "POST",
        body: { benevolence_records },
      }),
      invalidatesTags: ["FirmApplications"],
    }),
    firmStep6: builder.mutation<any, { id: string | number; referees: any[] }>({
      query: ({ id, referees }) => ({ url: `/firm/applications/${id}/step6`, method: "POST", body: { referees } }),
      invalidatesTags: ["FirmApplications"],
    }),
    // Takes no body: the server stamps the applicant's own chapter.
    firmStep7: builder.mutation<any, { id: string | number }>({
      query: ({ id }) => ({ url: `/firm/applications/${id}/step7`, method: "POST", body: {} }),
      invalidatesTags: ["FirmApplications"],
    }),
    firmAcknowledge: builder.mutation<any, { id: string | number }>({
      query: ({ id }) => ({ url: `/firm/applications/${id}/step9`, method: "POST" }),
      invalidatesTags: ["FirmApplications"],
    }),

    firmReopen: builder.mutation<any, { id: string | number }>({
      query: ({ id }) => ({ url: `/firm/applications/${id}/reopen`, method: "POST" }),
      invalidatesTags: ["FirmApplications"],
    }),
    firmResubmit: builder.mutation<any, { id: string | number }>({
      query: ({ id }) => ({ url: `/firm/applications/${id}/resubmit`, method: "POST" }),
      invalidatesTags: ["FirmApplications"],
    }),

    // Returns the OnePay payload, or a 422 with { message, blockers }.
    initiateFirmPayment: builder.mutation<any, void>({
      query: () => ({ url: "/firm/payment/initiate", method: "POST", body: {} }),
      invalidatesTags: ["FirmApplications"],
    }),
    // The donation agreed at the interview — same OnePay payload. The amount
    // comes off the application, not the request.
    initiateFirmDonation: builder.mutation<any, void>({
      query: () => ({ url: "/firm/donation/initiate", method: "POST", body: {} }),
      invalidatesTags: ["FirmApplications"],
    }),

    /* Closing out the interview by recording the donation agreed at it — this
       is what moves an application into "Donation Due". */
    setFirmDonation: builder.mutation<any, { id: string | number; amount_naira: number; remark?: string }>({
      query: ({ id, ...body }) => ({
        url: `/admin/firm-applications/${id}/donation`,
        method: "POST",
        body,
      }),
      invalidatesTags: (_r, _e, arg) => [
        "FirmApplications",
        { type: "FirmApplicationDetails" as const, id: String(arg.id) },
      ],
    }),

    /* Issuing the firm number — what actually registers the firm */
    getNextFirmNumber: builder.query<{ next: string; prefix: string; width: number }, void>({
      query: () => "/admin/firm-applications/next-firm-number",
      providesTags: ["FirmApplications"],
    }),
    issueFirmNumber: builder.mutation<any, { id: string | number; firm_no?: string; remark?: string }>({
      query: ({ id, firm_no, remark }) => ({
        url: `/admin/firm-applications/${id}/issue-number`,
        method: "POST",
        body: { firm_no, remark },
      }),
      invalidatesTags: (_r, _e, arg) => [
        "FirmApplications",
        // Issuing the number is what creates the firm, so the register has a
        // new entry the moment this succeeds.
        "Firms",
        { type: "FirmApplicationDetails" as const, id: String(arg.id) },
      ],
    }),

    /* Membership Committee — admin assignment side */
    getFirmCommitteePool: builder.query<any, void>({
      query: () => "/admin/firm-applications/committee/members",
    }),
    getFirmCommitteeReviews: builder.query<any[], string | number>({
      query: (id) => `/admin/firm-applications/${id}/committee/reviews`,
      transformResponse: (res: any) => res?.data ?? [],
      providesTags: (_r, _e, id) => [{ type: "FirmApplicationDetails" as const, id: String(id) }],
    }),
    assignFirmCommittee: builder.mutation<any, { id: string | number; member_ids: string[] }>({
      query: ({ id, member_ids }) => ({
        url: `/admin/firm-applications/${id}/committee/assign`,
        method: "POST",
        body: { member_ids },
      }),
      invalidatesTags: (_r, _e, arg) => [
        "FirmApplications",
        { type: "FirmApplicationDetails" as const, id: String(arg.id) },
      ],
    }),
    unassignFirmCommittee: builder.mutation<any, { id: string | number; reviewId: number }>({
      query: ({ id, reviewId }) => ({
        url: `/admin/firm-applications/${id}/committee/reviews/${reviewId}`,
        method: "DELETE",
      }),
      invalidatesTags: (_r, _e, arg) => [
        "FirmApplications",
        { type: "FirmApplicationDetails" as const, id: String(arg.id) },
      ],
    }),

    /* Membership Committee — the assigned member's own side */
    getMyFirmCommitteeReviews: builder.query<any[], void>({
      query: () => "/committee/my-firm-reviews",
      transformResponse: (res: any) => res?.data ?? [],
      providesTags: ["FirmCommitteeReviews"],
    }),
    decideFirmCommitteeReview: builder.mutation<any, { reviewId: number; decision: "approved" | "rejected"; remark?: string }>({
      query: ({ reviewId, decision, remark }) => ({
        url: `/committee/firm-reviews/${reviewId}/decide`,
        method: "POST",
        body: { decision, remark },
      }),
      invalidatesTags: ["FirmCommitteeReviews", "FirmApplications"],
    }),

    /* Partner picker — Member/Fellow only, flags already-committed members */
    searchFirmPartners: builder.query<any, string>({
      query: (q) => `/firm/partners/search/${encodeURIComponent(q)}`,
      transformResponse: (res: any) => (Array.isArray(res) ? res : res?.data ?? []),
    }),

    /* CV prefill for a member (from their upgrade application) */
    cvPrefill: builder.query<any, string>({
      query: (memberId) => `/firm/cv-prefill/${memberId}`,
      transformResponse: (res: any) => res?.data ?? res ?? {},
    }),

    /* Partner confirmation (the invited member's side) */
    getMyFirmPartnerRequests: builder.query<any[], { decision?: string } | void>({
      query: (arg) => {
        const d = (arg as any)?.decision;
        return `/firm/partner/requests${d ? `?decision=${encodeURIComponent(d)}` : ""}`;
      },
      transformResponse: (res: any) => (Array.isArray(res) ? res : res?.data ?? []),
      providesTags: ["FirmPartnerRequests"],
    }),
    acceptFirmPartnerRequest: builder.mutation<any, { partnerId: string | number; remarks?: string }>({
      query: ({ partnerId, remarks }) => ({
        url: `/firm/partner/requests/${partnerId}/accept`,
        method: "POST",
        body: remarks ? { remarks } : {},
      }),
      invalidatesTags: ["FirmPartnerRequests"],
    }),
    declineFirmPartnerRequest: builder.mutation<any, { partnerId: string | number; remarks: string }>({
      query: ({ partnerId, remarks }) => ({
        url: `/firm/partner/requests/${partnerId}/decline`,
        method: "POST",
        body: { remarks },
      }),
      invalidatesTags: ["FirmPartnerRequests"],
    }),

    /* Referee confirmation */
    getMyFirmRefereeRequests: builder.query<any[], { decision?: string } | void>({
      query: (arg) => {
        const d = (arg as any)?.decision;
        return `/firm/referee/requests${d ? `?decision=${encodeURIComponent(d)}` : ""}`;
      },
      transformResponse: (res: any) => (Array.isArray(res) ? res : res?.data ?? []),
      providesTags: ["FirmRefereeRequests"],
    }),
    acceptFirmRefereeRequest: builder.mutation<any, { refereeId: string | number; remarks?: string }>({
      query: ({ refereeId, remarks }) => ({
        url: `/firm/referee/requests/${refereeId}/accept`,
        method: "POST",
        body: remarks ? { remarks } : {},
      }),
      invalidatesTags: ["FirmRefereeRequests"],
    }),
    rejectFirmRefereeRequest: builder.mutation<any, { refereeId: string | number; remarks: string }>({
      query: ({ refereeId, remarks }) => ({
        url: `/firm/referee/requests/${refereeId}/reject`,
        method: "POST",
        body: { remarks },
      }),
      invalidatesTags: ["FirmRefereeRequests"],
    }),

    /* Admin review */
    adminFirmApplications: builder.query<any, { status?: string; page?: number; per_page?: number } | void>({
      query: (arg) => {
        const params = new URLSearchParams();
        if (arg?.status && arg.status !== "all") params.set("status", arg.status);
        if (arg?.page) params.set("page", String(arg.page));
        if (arg?.per_page) params.set("per_page", String(arg.per_page));
        const qs = params.toString();
        return `/admin/firm-applications${qs ? `?${qs}` : ""}`;
      },
      providesTags: ["FirmApplications"],
    }),
    adminFirmApplicationDetails: builder.query<any, string | number>({
      query: (id) => `/admin/firm-applications/${id}`,
      providesTags: (_r, _e, id) => [{ type: "FirmApplicationDetails" as const, id: String(id) }],
    }),

    /* ── The firm register ──────────────────────────────────────────────────
       Registered firms, not the applications that produced them. Paginated
       server-side, unlike the applications list: the register only grows, so a
       screen that pulled all of it would get slower every year. */
    adminFirms: builder.query<
      any,
      {
        page?: number;
        per_page?: number;
        search?: string;
        status?: string;
        chapter_id?: string;
        state_id?: string;
        approved_from?: string;
        approved_to?: string;
        no_partners?: boolean;
        no_account?: boolean;
        owing?: boolean;
        sort_by?: string;
        sort_dir?: string;
      } | void
    >({
      query: (arg) => {
        const params = new URLSearchParams();
        Object.entries(arg ?? {}).forEach(([k, v]) => {
          if (v !== undefined && v !== null && v !== "" && v !== false) {
            params.set(k, String(v));
          }
        });
        const qs = params.toString();
        return `/admin/firms${qs ? `?${qs}` : ""}`;
      },
      providesTags: ["Firms"],
    }),

    /**
     * Give a firm its own account — which is also what raises its outstanding
     * legacy dues invoice. A firm with no account cannot be billed at all
     * (invoices.user_id is NOT NULL), so the two are one action.
     */
    createFirmAccount: builder.mutation<
      any,
      { id: number | string; email: string; name?: string }
    >({
      query: ({ id, ...body }) => ({
        url: `/admin/firms/${id}/account`,
        method: "POST",
        body,
      }),
      invalidatesTags: ["Firms"],
    }),

    /* ── Money a firm paid at the bank ──────────────────────────────────────
       A firm's invoices belong to no user, so they appear in none of the
       member-scoped invoice screens — the admin records against the FIRM. */
    getFirmPayments: builder.query<any, string | number>({
      query: (id) => `/admin/firms/${id}/payments`,
      providesTags: (_r, _e, id) => [{ type: "Firms" as const, id: String(id) }],
    }),
    recordFirmPayment: builder.mutation<any, { id: string | number; body: FormData }>({
      query: ({ id, body }) => ({
        url: `/admin/firms/${id}/payments`,
        method: "POST",
        // FormData — the teller/proof is a file upload, so no JSON header.
        body,
      }),
      invalidatesTags: ["Firms"],
    }),

    /** Counts for the whole register, not the page on screen. */
    adminFirmsSummary: builder.query<
      {
        total: number;
        active: number;
        inactive: number;
        no_partners: number;
        no_account: number;
        /** Firms carrying arrears, and what they add up to in kobo. */
        owing: number;
        owing_kobo: number;
      },
      {
        search?: string;
        chapter_id?: string;
        state_id?: string;
        approved_from?: string;
        approved_to?: string;
        no_partners?: boolean;
        no_account?: boolean;
        owing?: boolean;
      } | void
    >({
      query: (arg) => {
        const params = new URLSearchParams();
        Object.entries(arg ?? {}).forEach(([k, v]) => {
          if (v !== undefined && v !== null && v !== "" && v !== false) {
            params.set(k, String(v));
          }
        });
        const qs = params.toString();
        return `/admin/firms/summary${qs ? `?${qs}` : ""}`;
      },
      providesTags: ["Firms"],
    }),
    updateFirmApplicationStatus: builder.mutation<any, { id: string | number; status: string; remark?: string }>({
      // POST (not PATCH): some shared hosts block PATCH before it reaches the
      // app, surfacing as a CORS/network error. The route accepts both.
      query: ({ id, ...body }) => ({ url: `/admin/firm-applications/${id}/status`, method: "POST", body }),
      invalidatesTags: (_r, _e, arg) => [
        "FirmApplications",
        { type: "FirmApplicationDetails" as const, id: String(arg.id) },
      ],
    }),

    /* Chapter endorsement (chairman/secretary) */
    getChapterFirmApplications: builder.query<any, { status?: string } | void>({
      query: (arg) => `/chapter/my-firm-applications${arg?.status ? `?status=${encodeURIComponent(arg.status)}` : ""}`,
      providesTags: ["FirmApplications"],
    }),
    getChapterFirmApplicationDetails: builder.query<any, string | number>({
      query: (id) => `/chapter/firm-applications/${id}`,
      providesTags: (_r, _e, id) => [{ type: "FirmApplicationDetails" as const, id: String(id) }],
    }),
    chapterAcceptFirmApplication: builder.mutation<any, { id: string | number; comment: string }>({
      query: ({ id, comment }) => ({ url: `/chapter/firm-applications/${id}/accept`, method: "POST", body: { comment } }),
      invalidatesTags: (_r, _e, arg) => [
        "FirmApplications",
        { type: "FirmApplicationDetails" as const, id: String(arg.id) },
      ],
    }),
    chapterRejectFirmApplication: builder.mutation<any, { id: string | number; reason?: string }>({
      query: ({ id, reason }) => ({ url: `/chapter/firm-applications/${id}/reject`, method: "POST", body: { reason } }),
      invalidatesTags: (_r, _e, arg) => [
        "FirmApplications",
        { type: "FirmApplicationDetails" as const, id: String(arg.id) },
      ],
    }),

    /* Document upload — shared generic uploader */
    uploadFirmDocument: builder.mutation<{ path: string; url?: string }, FormData>({
      query: (body) => ({ url: "/upgrade-documents", method: "POST", body, formData: true }),
    }),
  }),
  overrideExisting: false,
});

export const {
  useGetFirmEligibilityQuery,
  useGetMyFirmApplicationsQuery,
  useGetFirmApplicationDetailsQuery,
  useGetFirmCurrentQuery,
  useFirmStep1Mutation,
  useFirmStep5Mutation,
  useFirmStep6Mutation,
  useFirmStep7Mutation,
  useFirmAcknowledgeMutation,
  useFirmReopenMutation,
  useFirmResubmitMutation,
  useInitiateFirmPaymentMutation,
  useInitiateFirmDonationMutation,
  useSetFirmDonationMutation,
  useGetNextFirmNumberQuery,
  useIssueFirmNumberMutation,
  useGetFirmCommitteePoolQuery,
  useGetFirmCommitteeReviewsQuery,
  useAssignFirmCommitteeMutation,
  useUnassignFirmCommitteeMutation,
  useGetMyFirmCommitteeReviewsQuery,
  useDecideFirmCommitteeReviewMutation,
  useSearchFirmPartnersQuery,
  useLazySearchFirmPartnersQuery,
  useLazyCvPrefillQuery,
  useGetMyFirmPartnerRequestsQuery,
  useAcceptFirmPartnerRequestMutation,
  useDeclineFirmPartnerRequestMutation,
  useGetMyFirmRefereeRequestsQuery,
  useAcceptFirmRefereeRequestMutation,
  useRejectFirmRefereeRequestMutation,
  useAdminFirmApplicationsQuery,
  useAdminFirmApplicationDetailsQuery,
  useGetMyFirmsQuery,
  useGetMyFirmQuery,
  usePayMyFirmDuesMutation,
  useAdminFirmsQuery,
  useAdminFirmsSummaryQuery,
  useCreateFirmAccountMutation,
  useGetFirmPaymentsQuery,
  useRecordFirmPaymentMutation,
  useUpdateFirmApplicationStatusMutation,
  useGetChapterFirmApplicationsQuery,
  useGetChapterFirmApplicationDetailsQuery,
  useChapterAcceptFirmApplicationMutation,
  useChapterRejectFirmApplicationMutation,
  useUploadFirmDocumentMutation,
} = firmApi;
